import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { QueryOf } from "../types";
import type { DeploymentCreateBody, DeploymentRollbackBody } from "./types";

export type DeploymentFilter = QueryOf<"listProjectDeployments">;

export function useDeployments(project: string, filter: DeploymentFilter = {}) {
  return useQuery({
    queryKey: qk.deployments(project, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/projects/{project}/deployments", { params: { path: { project }, query: filter } })),
    refetchInterval: 20_000,
    enabled: !!project,
  });
}

export function useDeployment(deploymentId: string) {
  return useQuery({
    queryKey: qk.deployment(deploymentId),
    queryFn: () =>
      unwrap(api.GET("/v1/deployments/{deployment_id}", { params: { path: { deployment_id: deploymentId } } })),
    enabled: !!deploymentId,
  });
}

/** Plain-English cause and fix for a failed deploy. `ai_generated` tells you to show <AILabel>. */
export function useDeploymentExplanation(deploymentId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.deploymentExplanation(deploymentId),
    queryFn: () =>
      unwrap(
        api.GET("/v1/deployments/{deployment_id}/explanation", { params: { path: { deployment_id: deploymentId } } }),
      ),
    enabled: !!deploymentId && (opts.enabled ?? true),
  });
}

export function useDeploymentLogs(deploymentId: string) {
  return useQuery({
    queryKey: qk.deploymentLogs(deploymentId),
    queryFn: () =>
      unwrap(api.GET("/v1/deployments/{deployment_id}/logs", { params: { path: { deployment_id: deploymentId } } })),
    enabled: !!deploymentId,
  });
}

export function useBuild(buildId: string | null | undefined) {
  return useQuery({
    queryKey: qk.build(buildId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/builds/{build_id}", { params: { path: { build_id: buildId! } } })),
    enabled: !!buildId,
  });
}

export function useCreateDeployment(project: string, environment: string, service: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeploymentCreateBody) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/services/{service}/deployments", {
          params: { path: { project, environment, service }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(project) }),
  });
}

export function useRollbackDeployment(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deploymentId, body }: { deploymentId: string; body?: DeploymentRollbackBody }) =>
      unwrap(
        api.POST("/v1/deployments/{deployment_id}/rollback", {
          params: { path: { deployment_id: deploymentId }, header: idem() },
          body: body ?? {},
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(project) }),
  });
}

export function useCancelDeployment(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) =>
      unwrap(api.POST("/v1/deployments/{deployment_id}/cancel", { params: { path: { deployment_id: deploymentId }, header: idem() } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(project) }),
  });
}
